import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnInit, Renderer2, ViewChild, inject } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { OnChange } from '@df/ng/on-change';
import { AbstractPlayerComponent } from '@df/ui/display/media/abstract-player.component';

/**
 * @example
 *
 * ```html
 *   <video-player video-url="https://www.w3schools.com/html/mov_bbb.mp4"
 *                 autoplay-in-viewport="true"
 *                 loop="true"
 *                 muted="true"
 *                 poster="https://place-hold.it/300">
 *   </video-player>
 * ```
 */
@Component({
    selector: 'video-player',
    template: `
        <video class="video-player-container" [attr.preload]="preload" [attr.poster]="poster" *ngIf="!!src" #container>
            <source [attr.src]="src" type='video/mp4;codecs="avc1.42E01E,mp4a.40.2"' />
        </video>
        <div
            class="video-player-control"
            [class.is-playing]="isPlaying"
            *ngIf="(!background && ((!autoplay && !autoplayInViewport) || isPlaying || showControl)) || controls"
        >
            <div class="icon-control icon-video-play" *ngIf="playButton && !isPlaying" (click)="play()">
                <ng-container *ngTemplateOutlet="playButton; context: { $implicit: this }"></ng-container>
            </div>
            <div class="icon-control icon-video-pause" *ngIf="pauseButton && isPlaying" (click)="pause()">
                <ng-container *ngTemplateOutlet="pauseButton; context: { $implicit: this }"></ng-container>
            </div>
        </div>
        <ng-container *ngTemplateOutlet="customTpl!; context: { $implicit: this }"></ng-container>
    `,
    standalone: true,
    imports: [CommonModule]
})
export class VideoPlayerComponent extends AbstractPlayerComponent<VideoPlayerComponent> implements OnInit {
    /** @internal */
    protected override componentId: string = 'video-player';

    /**
     * Preload video when paused
     * @type {boolean}
     */
    @Input('preload')
    preload?: boolean;

    /**
     * Video poster image
     * @type {string}
     */
    @Input('poster')
    set poster(value: SafeResourceUrl | string | undefined) {
        this._poster = value;
    }
    get poster(): SafeResourceUrl | string | undefined {
        return this.domSanitizer.bypassSecurityTrustResourceUrl(<string>this._poster);
    }
    protected _poster: SafeResourceUrl | string | undefined;

    /**
     * Display video as background - autoplay, loop and no controls.
     *
     * @type {boolean}
     */
    @Input('background')
    @OnChange<boolean>(function (this: VideoPlayerComponent, value: boolean) {
        (this.renderer2 as Renderer2).setStyle(this.elementRef.nativeElement, 'pointerEvents', value ? 'none' : null);
        value
            ? (this.renderer2 as Renderer2).addClass(this.elementRef.nativeElement, 'is-background')
            : (this.renderer2 as Renderer2).removeClass(this.elementRef.nativeElement, 'is-background');
    })
    background?: boolean;

    @Input('controls')
    controls?: boolean;

    @Input('muted')
    muted?: boolean;

    @ViewChild('container')
    container?: ElementRef;

    private renderer2 = inject(Renderer2);

    override ngAfterViewInit() {
        if (this.autoplay || this.autoplayInViewport || this.playerInit || this.useCustomControls) {
            if (this.useCustomControls) {
                this.setPlayer(() => this.setControlsListeners());
            } else {
                this.setPlayer();
            }

            if (this.playerInit) {
                this.playerInit.emit(this);
            }
        }

        super.ngAfterViewInit();
    }

    /**
     * Update ID.
     */
    override set id(value: string) {
        this._id = (this.videoUrl || this.videoId)!;
    }

    /**
     * Get video ID from `this.videoId` or `this.videoUrl`.
     *
     * @returns {string}
     */
    override get id(): string {
        return this._id;
    }

    /**
     * Get player iframe URL.
     *
     * @returns {SafeResourceUrl | undefined}
     */
    get src(): SafeResourceUrl | undefined {
        return this.domSanitizer.bypassSecurityTrustResourceUrl(this.id);
    }

    override update() {}

    /**
     * Play video
     */
    override play() {
        if (this.player) {
            this.player.play();
            this.isPlaying = true;
            this.isPaused = false;

            if (this.playerPlay) {
                this.playerPlay.emit(this);
            }

            this.detectChanges();
        }
    }

    override pause() {
        if (this.player) {
            this.player.pause();
            this.isPlaying = false;
            this.isPaused = true;

            if (this.playerPause) {
                this.playerPause.emit(this);
            }

            this.detectChanges();
        }
    }

    /**
     * Stop video
     */
    override stop() {
        if (this.player) {
            this.pause();
            this.player.currentTime = 0;
            this.isPaused = false;

            if (this.playerStop) {
                this.playerStop.emit(this);
            }

            this.detectChanges();
        }
    }

    override mute() {
        if (this.player) {
            this.player.muted = true;
            this.isMuted = true;
            this.detectChanges();
        }
    }

    override unMute() {
        if (this.player) {
            this.player.muted = false;
            this.isMuted = false;
            this.detectChanges();
        }
    }

    /**
     * Setup player.
     *
     * @param callback {() => void}
     */
    setPlayer(callback?: () => void) {
        if (!this.player) {
            this.player = this.container?.nativeElement;

            // Hacks for Mobile Safari
            this.renderer2.setAttribute(this.player, 'playsinline', 'true');
            this.renderer2.setAttribute(this.player, 'controls', 'true');

            this.timeoutService.setTimeout(() => {
                this.renderer2.removeAttribute(this.player, 'controls');
            });

            if (this.muted) {
                this.mute();
            }

            this.player.addEventListener('ended', () => {
                this.stop();

                if (this.loop) {
                    this.play();
                }
            });

            this.player.addEventListener('loadeddata', () => {
                if (!this.useCustomControls) {
                    this.checkInView();
                } else if (callback) {
                    callback();
                }
            });
        } else if (callback) {
            callback();
        }
    }
}
